import config from 'config';

import { getFullProductsQuery } from '~/builder/queries/grid';
import {
  transformCioFacets,
  transformCioResults,
  getSanitizedFilterInputsForCio,
  getPreFilterExpressions,
  getCioSortOrder,
  isolateSizeFilters,
  setImageServiceForRelatedProducts,
} from '~/grid/utils/cioUtils';
import { buildProductDimensionsQueryFragment } from '~/grid/utils/getProductDimensionsQuery';
import { getProductGridQueryParameters } from '~/grid/utils/getProductGridQueryParameters';
import {
  getGqlPricingMap,
  getGqlRelatedProductMap,
} from '~/grid/utils/gqlUtils';
import { notifySentryNorthstar } from '~/grid/utils/northstarUtil';
import {
  DEFAULT_GRID_ES_QUERY_FIELDS,
  DEFAULT_GRID_PRODUCT_FIELDS,
} from '~/shared/constants/products';
import {
  parseGqlProductsToV2,
  transformDimensions,
} from '~/techstyle-shared/react-graphql';

/**
 * @function filterFutureProducts
 * @description Filters out related products (for grid swatches) that have a
 * expected release date in the future
 * @param {Object} products Collection of related products returned via GraphQL
 * @param {Object} staticCurrentDate The current date (Date) defined at runtime
 * of the original product query
 * @returns {Object} Filtered collection of currently active related products
 */
export function filterFutureProducts({
  products,
  staticCurrentDate = new Date(),
}) {
  const filteredProducts = {};

  for (const productId in products) {
    const relatedProductList =
      products[productId]?.relatedProductIdObjectList || [];

    // filter out entries where dateExpectedInMs is ahead of the current time
    const filteredRelatedProductList = relatedProductList.filter((product) => {
      const dateExpectedInMs = new Date(product?.dateExpected).getTime();
      return dateExpectedInMs <= staticCurrentDate.getTime();
    });

    filteredProducts[productId] = {
      relatedProductIdObjectList: filteredRelatedProductList,
    };
  }

  return filteredProducts;
}

export const getCioSearchProducts = async ({
  page,
  pageSize,
  payload,
  searchQuery,
  cioClient,
  apolloClient,
  isElite,
  isVip,
  shouldShowWarehouseProducts = false,
  shouldHideOriginRestricted = false,
  shouldUsePricingService = false,
  shouldUseImageService = false,
  domain,
}) => {
  const staticCurrentDate = new Date();
  const { sizeFilters, nonSizeFilters } = isolateSizeFilters(
    payload?.aggregationFilter
  );

  const baseFilters = payload?.aggregations;

  const hiddenFacets = config.get('public.brand.constructor.hiddenFacets');

  const searchParameters = {
    resultsPerPage: pageSize,
    preFilterExpression: getPreFilterExpressions({
      isElite,
      isVip,
      dateNowInMs: staticCurrentDate.getTime(),
      shouldShowWarehouseProducts,
      shouldHideOriginRestricted: shouldHideOriginRestricted,
      selectedSizeFilters: sizeFilters,
    }),
    filters: getSanitizedFilterInputsForCio(nonSizeFilters),
    sortBy: payload?.sort,
    sortOrder: getCioSortOrder(payload?.sort),
    page,
  };

  if (hiddenFacets?.length) {
    searchParameters.hiddenFacets = hiddenFacets;
  }

  const results = await cioClient.search.getSearchResults(
    searchQuery,
    searchParameters
  );

  const cioResults = await transformCioResults({
    results: results?.response?.results,
    shouldUseImageService,
  });
  const totalResults = results?.response?.total_num_results ?? 0;
  const transformedCioFilterSettings = transformCioFacets({
    enableFacetLimit: true,
    facets: results?.response?.facets,
    requestFilters: results?.request?.filters,
    selectedSizeFilters: sizeFilters,
    baseFilters,
    domain,
  });

  const gqlPromises = [
    getGqlPricingMap({
      products: cioResults,
      apolloClient,
      isVip,
      shouldUsePricingService,
    }),
    getGqlRelatedProductMap({
      products: cioResults,
      apolloClient,
      shouldShowWarehouseProducts,
    }),
  ];

  const [gqlPricingMap, gqlRelatedProductMap] = await Promise.all(gqlPromises);

  const filteredGqlRelatedProductMap = filterFutureProducts({
    products: gqlRelatedProductMap,
    staticCurrentDate,
  });

  const relatedProductsMap = shouldUseImageService
    ? setImageServiceForRelatedProducts({
        relatedProductsMap: filteredGqlRelatedProductMap,
      })
    : filteredGqlRelatedProductMap;

  const productsWithPricingAndRelatedProducts = cioResults?.map((product) => {
    return {
      ...product,
      ...gqlPricingMap[product.masterProductId],
      ...relatedProductsMap[product.masterProductId],
    };
  });

  const payloads = [
    {
      aggregations: transformedCioFilterSettings,
      page,
      pageSize,
      pages: Math.ceil(totalResults / pageSize),
      products: productsWithPricingAndRelatedProducts,
      total: totalResults,
    },
  ];

  return payloads;
};

export const getCioBrowseProducts = async ({
  page,
  pageSize,
  payload,
  cioFilterName,
  cioFilterValue,
  cioClient,
  apolloClient,
  isElite,
  isVip,
  shouldShowWarehouseProducts = false,
  shouldHideOriginRestricted = false,
  shouldUsePricingService = false,
  shouldUseImageService = false,
  isSaleGrid = false,
  domain,
  surfaceHiddenFacets = false,
}) => {
  const staticCurrentDate = new Date();
  const { sizeFilters, nonSizeFilters } = isolateSizeFilters(
    payload?.aggregationFilter
  );

  const baseFilters = payload?.aggregations;

  const hiddenFacets = config.get('public.brand.constructor.hiddenFacets');
  const browseParameters = {
    resultsPerPage: pageSize,
    preFilterExpression: getPreFilterExpressions({
      isElite,
      isVip,
      dateNowInMs: staticCurrentDate.getTime(),
      shouldShowWarehouseProducts,
      shouldHideOriginRestricted: shouldHideOriginRestricted,
      selectedSizeFilters: sizeFilters,
      autoExcludeClearanceFplIds: !isSaleGrid,
    }),
    filters: getSanitizedFilterInputsForCio(nonSizeFilters),
    sortBy: payload?.sort,
    sortOrder: getCioSortOrder(payload?.sort),
    page,
  };

  if (surfaceHiddenFacets && hiddenFacets?.length) {
    browseParameters.hiddenFacets = hiddenFacets;
  }

  const results = await cioClient.browse.getBrowseResults(
    cioFilterName,
    cioFilterValue,
    browseParameters
  );

  const cioResults = await transformCioResults({
    results: results?.response?.results,
    shouldUseImageService,
  });
  const totalResults = results?.response?.total_num_results ?? 0;
  const transformedCioFilterSettings = transformCioFacets({
    facets: results?.response?.facets,
    requestFilters: results?.request?.filters,
    selectedSizeFilters: sizeFilters,
    baseFilters,
    domain,
  });

  const gqlPromises = [
    getGqlPricingMap({
      products: cioResults,
      apolloClient,
      isVip,
      shouldUsePricingService,
    }),
    getGqlRelatedProductMap({
      products: cioResults,
      apolloClient,
      shouldShowWarehouseProducts,
    }),
  ];

  const [gqlPricingMap, gqlRelatedProductMap] = await Promise.all(gqlPromises);

  const filteredGqlRelatedProductMap = filterFutureProducts({
    products: gqlRelatedProductMap,
    staticCurrentDate,
  });

  const relatedProductsMap = shouldUseImageService
    ? setImageServiceForRelatedProducts({
        relatedProductsMap: filteredGqlRelatedProductMap,
      })
    : filteredGqlRelatedProductMap;

  const productsWithPricingAndRelatedProducts = cioResults?.map((product) => {
    return {
      ...product,
      ...gqlPricingMap[product.masterProductId],
      ...relatedProductsMap[product.masterProductId],
    };
  });

  const payloads = [
    {
      aggregations: transformedCioFilterSettings,
      page,
      pageSize,
      pages: Math.ceil(totalResults / pageSize),
      products: productsWithPricingAndRelatedProducts,
      total: totalResults,
    },
  ];

  return payloads;
};

export const getV2Products = async ({
  page,
  pageSize,
  payload,
  retailDiscounting,
  actions,
  productSource,
}) => {
  const params = {
    page,
    size: pageSize,
    retailDiscounting,
    ...payload,
    // NOTE: restricts product fields returned in order to
    // decrease payload size from API
    productFields: DEFAULT_GRID_PRODUCT_FIELDS,
    fields: DEFAULT_GRID_ES_QUERY_FIELDS,
  };

  const requests = [];
  requests.push(actions.loadProducts(params, { apiVersion: 2, productSource }));

  const responses = await Promise.all(requests);
  const payloads = responses.map((response) => response?.payload);
  return payloads;
};

export const getGqlProducts = async ({
  apolloClient,
  page,
  pageSize,
  payload,
  retailDiscounting,
}) => {
  let productDimensionsData;
  let productListingData;

  const { variables } = getProductGridQueryParameters({
    page,
    size: pageSize,
    ...payload,
    retailDiscounting,
    fields: DEFAULT_GRID_ES_QUERY_FIELDS,
  });

  try {
    // for fully internal grids powered by federated graph we can do this
    const {
      data: { productDimensions, productListing },
    } = await apolloClient.query({
      context: { clientName: 'supergraph' },
      query: getFullProductsQuery(
        buildProductDimensionsQueryFragment({
          page,
          size: pageSize,
          ...payload,
          retailDiscounting,
          fields: DEFAULT_GRID_ES_QUERY_FIELDS,
        })
      ),
      variables,
    });
    productDimensionsData = productDimensions;
    productListingData = productListing;
  } catch (e) {
    notifySentryNorthstar(
      e,
      getFullProductsQuery(
        buildProductDimensionsQueryFragment({
          page,
          size: pageSize,
          ...payload,
          retailDiscounting,
          fields: DEFAULT_GRID_ES_QUERY_FIELDS,
        })
      ),
      variables
    );
  }

  const payloads = [
    {
      products: parseGqlProductsToV2(productListingData?.products),
      aggregations: productDimensionsData?.dimensions
        ? transformDimensions(productDimensionsData.dimensions)
        : [],
      total: productListingData?.total?.value ?? 0,
      page,
      size: pageSize,
    },
  ];

  return payloads;
};
